import { z } from 'zod';
import type { BooleanFlag, ModuleKey } from '../enums';
import { idSchema, localDateSchema } from './common';
import { idempotencyKeySchema, mcpNoteArg } from './mcp-base';
import { QUICK_NOTE_COLORS, QUICK_NOTE_LIST_MAX, QUICK_NOTE_MAX_CHARS, QUICK_NOTE_STATES } from './quick-notes';

/**
 * Szybkie notatki (karteczki na „Dziś”) przez MCP (DECISIONS 2026-10-17 [P] „Szybkie notatki na »Dziś«”: „Masz mieć do
 * nich dostęp”): konektor osoby czyta i dodaje JEJ notatki — `list_quick_notes`, `add_quick_note`, `update_quick_note`,
 * `complete_quick_note`. Notatki są prywatne: token widzi wyłącznie notatki swojej osoby (cudze = NOT_FOUND, także
 * dla tokenu właściciela workspace'u); token z zakresem klientów / projektów (bez rekordów wewnętrznych) = FORBIDDEN.
 * Bez modułu (jak Dziś). Usługi: `packages/core/src/modules/notes`, adaptery: `packages/core/src/mcp/tools/quick-notes.ts`.
 *
 * Konwencje jak w `mcp-daily.ts`: snake_case, mutacje z `idempotency_key` i opcjonalnym `note` (prośba osoby do Historii
 * AI), edycja z opcjonalnym `expected_version`.
 */

interface McpQuickNotesToolSpec {
  name: string;
  version: number;
  title: string;
  description: string;
  mutating: boolean;
  createsApproval: boolean;
  module: ModuleKey | null;
  level: 'view' | 'edit';
  flags: readonly BooleanFlag[];
  runToken: boolean;
  input: z.ZodTypeAny;
}

function tool<const N extends string>(spec: McpQuickNotesToolSpec & { name: N }): McpQuickNotesToolSpec & { name: N } {
  return spec;
}

const bodyArg = z.string().trim().min(1).max(QUICK_NOTE_MAX_CHARS);

export const listQuickNotesArgs = z
  .object({
    /** open = na Dziś (domyślnie), done = odhaczone, all = obie */
    state: z.enum(QUICK_NOTE_STATES).default('open'),
    limit: z.number().int().min(1).max(QUICK_NOTE_LIST_MAX).default(50),
  })
  .strict();

export const addQuickNoteArgs = z
  .object({
    body: bodyArg,
    color: z.enum(QUICK_NOTE_COLORS).optional(),
    /** „na kiedy” RRRR-MM-DD (Europe/Warsaw) */
    due_date: localDateSchema.optional(),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

export const updateQuickNoteArgs = z
  .object({
    note_id: idSchema,
    expected_version: z.number().int().positive().optional(),
    body: bodyArg.optional(),
    color: z.enum(QUICK_NOTE_COLORS).optional(),
    /** null = bez terminu */
    due_date: localDateSchema.nullable().optional(),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict()
  .refine((v) => v.body !== undefined || v.color !== undefined || v.due_date !== undefined, {
    message: 'Podaj body, color albo due_date',
    path: ['body'],
  });

export const completeQuickNoteArgs = z
  .object({
    note_id: idSchema,
    /** false = cofnij odhaczenie (notatka wraca na Dziś) */
    done: z.boolean().default(true),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

/** Notatka w wyniku narzędzi. */
export interface McpQuickNote {
  id: string;
  body: string;
  color: string;
  due_date: string | null;
  overdue: boolean;
  done: boolean;
  done_at: string | null;
  source: string;
  task_id: string | null;
  created_at: string;
  updated_at: string;
  version: number;
}

export const MCP_QUICK_NOTES_TOOLS = [
  tool({
    name: 'list_quick_notes',
    version: 1,
    title: 'Szybkie notatki',
    description:
      'Szybkie notatki osoby (karteczki na „Dziś”, prywatne): treść, kolor, „na kiedy” (overdue = po terminie), stan. Domyślnie otwarte; state: done = odhaczone, all = obie. To nie zadania.',
    mutating: false,
    createsApproval: false,
    module: null,
    level: 'view',
    flags: [],
    runToken: false,
    input: listQuickNotesArgs,
  }),
  tool({
    name: 'add_quick_note',
    version: 1,
    title: 'Dodaj szybką notatkę',
    description:
      'Dodaje karteczkę na „Dziś” osoby (np. „pojechać na pocztę”): treść do 1000 znaków, kolor (yellow domyślnie, pink, blue, green), opcjonalnie due_date. Prywatna — zespół jej nie widzi. Do pracy dla klienta użyj create_task.',
    mutating: true,
    createsApproval: false,
    module: null,
    level: 'edit',
    flags: [],
    runToken: false,
    input: addQuickNoteArgs,
  }),
  tool({
    name: 'update_quick_note',
    version: 1,
    title: 'Zmień szybką notatkę',
    description:
      'Zmienia treść, kolor albo „na kiedy” (due_date: null = bez terminu) karteczki osoby. expected_version z list_quick_notes (inna = CONFLICT).',
    mutating: true,
    createsApproval: false,
    module: null,
    level: 'edit',
    flags: [],
    runToken: false,
    input: updateQuickNoteArgs,
  }),
  tool({
    name: 'complete_quick_note',
    version: 1,
    title: 'Odhacz szybką notatkę',
    description:
      'Odhacza karteczkę osoby (znika z „Dziś”; nic nie jest usuwane). done: false = cofnij odhaczenie (karteczka wraca).',
    mutating: true,
    createsApproval: false,
    module: null,
    level: 'edit',
    flags: [],
    runToken: false,
    input: completeQuickNoteArgs,
  }),
] as const;
